import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import API, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { IdentificationBadge, ArrowLeft, UsersThree } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ClockCard } from "../components/hr/ClockCard";
import { WeekTimesheet } from "../components/hr/WeekTimesheet";
import { LeaveTab } from "../components/hr/LeaveTab";
import { SicknessTab } from "../components/hr/SicknessTab";
import { InvoicesTab } from "../components/hr/InvoicesTab";
import { hm } from "../components/hr/hrUtils";

const TABS = ["time", "leave", "sickness", "invoices"];

/** Personal HR portal: clock, hours vs contracted, leave, sickness, monthly invoices. Ops Directors open anyone's via ?user_id. */
export default function MyHRPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const viewId = params.get("user_id") || "";
  const tab = TABS.includes(params.get("tab")) ? params.get("tab") : "time";
  const [me, setMe] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const isSelf = !viewId || viewId === user?._id;

  const load = useCallback(async () => {
    try { const { data } = await API.get(`/hr/me${viewId ? `?user_id=${viewId}` : ""}`); setMe(data); setRefresh((x) => x + 1); }
    catch (e) { toast.error(formatApiError(e)); }
  }, [viewId]);
  useEffect(() => { load(); }, [load]);

  const setTab = (t) => { const p = new URLSearchParams(params); p.set("tab", t); setParams(p, { replace: true }); };
  const isOps = !!me?.is_ops;

  return (
    <div className="space-y-6" data-testid="hr-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          {!isSelf && <Link to="/hr/team" className="text-xs text-muted-foreground hover:underline flex items-center gap-1 mb-1"><ArrowLeft size={12} /> HR Hub</Link>}
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><IdentificationBadge className="h-6 w-6" weight="duotone" /> {isSelf ? "My HR" : `${me?.user?.name || "…"} — HR`}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isSelf ? "Your time, annual leave, sickness and monthly invoices. Only you and the Operations Directors can see this." : "Viewing as Operations Director — you can correct time, book leave and record sickness for this person."}
          </p>
        </div>
        {isOps && isSelf && <Link to="/hr/team" className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted" data-testid="hr-open-hub"><UsersThree className="h-4 w-4" /> HR Hub{(me.pending_counts.leave + me.pending_counts.invoices) > 0 && <span className="rounded-full bg-primary text-primary-foreground text-xs px-2">{me.pending_counts.leave + me.pending_counts.invoices}</span>}</Link>}
      </div>

      <div className="grid lg:grid-cols-[320px_minmax(0,1fr)] gap-5 items-start">
        <div className="space-y-4">
          {isSelf ? <ClockCard me={me} onChange={load} /> : (
            <div className="rounded-xl border border-border bg-card p-5" data-testid="hr-clock-readonly">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">This week</p>
              <p className="text-3xl font-bold tabular-nums mt-1">{hm(me?.week?.total_minutes)}</p>
              <p className="text-xs text-muted-foreground">of {hm(me?.week?.contracted_minutes)} contracted{me?.open_entry ? ` · clocked in now` : ""}</p>
            </div>
          )}
          <div className="rounded-xl border border-border bg-card p-4 text-sm space-y-1.5" data-testid="hr-summary-card">
            <div className="flex justify-between"><span className="text-muted-foreground">Leave remaining</span><strong className="tabular-nums">{me?.leave?.remaining ?? "—"}d of {me?.leave?.allowance ?? "—"}</strong></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Contracted</span><strong className="tabular-nums">{me?.profile?.contracted_hours_per_week ?? "—"}h / week</strong></div>
            {me?.open_sickness && <div className="flex justify-between text-red-700"><span>Off sick</span><strong>since {me.open_sickness.start_date.slice(8, 10)}/{me.open_sickness.start_date.slice(5, 7)}</strong></div>}
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-4 w-full sm:w-auto">
            <TabsTrigger value="time" data-testid="hr-tab-time">Time</TabsTrigger>
            <TabsTrigger value="leave" data-testid="hr-tab-leave">Leave</TabsTrigger>
            <TabsTrigger value="sickness" data-testid="hr-tab-sickness">Sickness</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="hr-tab-invoices">Invoices</TabsTrigger>
          </TabsList>
          <TabsContent value="time" className="mt-4"><WeekTimesheet userId={viewId || undefined} canEdit={isOps} refreshKey={refresh} /></TabsContent>
          <TabsContent value="leave" className="mt-4"><LeaveTab userId={viewId || undefined} isOps={isOps} isSelf={isSelf} onChanged={load} /></TabsContent>
          <TabsContent value="sickness" className="mt-4"><SicknessTab userId={viewId || undefined} isSelf={isSelf} onChanged={load} /></TabsContent>
          <TabsContent value="invoices" className="mt-4"><InvoicesTab userId={viewId || undefined} isOps={isOps} isSelf={isSelf} profile={me?.profile} onChanged={load} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
